// AvailabilityDot / AvailabilityBadge — the courtesy signal, one place.
// A dot rides an avatar wherever a person is shown; the emoji + label go inline
// where there's room (DM header, the Details member list). Deliberately quiet: this
// tells a colleague whether now is a good moment, it is not a dashboard.
import React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { AVAILABILITY_DOT_CLASS, availabilityTone, availabilityTooltip } from '../availability';
import type { Availability } from '../types';

/** The one place the leave provenance sentence is worded. */
export function useAvailabilityTooltip(): (a?: Availability | null) => string {
  const { t } = useTranslation('conversations');
  const fromLeave = t('availability_from_leave', { defaultValue: 'From approved time off' });
  return (a) => availabilityTooltip(a, { fromLeave });
}

interface DotProps {
  availability?: Availability | null;
  /** px — matches the avatar it sits on. */
  size?: number;
  /** Ring it against the surface behind it (avatars overlap the card). */
  ring?: boolean;
  className?: string;
}

export const AvailabilityDot: React.FC<DotProps> = ({ availability, size = 10, ring = true, className }) => {
  const tooltip = useAvailabilityTooltip();
  if (!availability) return null;
  const title = tooltip(availability);
  return (
    <span
      data-testid="availability-dot"
      data-tone={availabilityTone(availability)}
      title={title}
      aria-label={title}
      role="img"
      style={{ height: size, width: size }}
      className={cn(
        'inline-block flex-none rounded-full',
        ring ? 'border-2 border-card' : '',
        AVAILABILITY_DOT_CLASS[availabilityTone(availability)],
        className,
      )}
    />
  );
};

/** An avatar (or anything) with the dot pinned to its bottom-right corner. */
export const AvailabilityDotOverlay: React.FC<{
  availability?: Availability | null;
  children: React.ReactNode;
  size?: number;
  className?: string;
}> = ({ availability, children, size = 10, className }) => (
  <span className={cn('relative inline-flex flex-none', className)}>
    {children}
    {availability && (
      <AvailabilityDot availability={availability} size={size} className="absolute -bottom-0.5 -right-0.5" />
    )}
  </span>
);

interface BadgeProps {
  availability?: Availability | null;
  /** Hide the words on narrow surfaces and keep only the emoji. */
  compact?: boolean;
  className?: string;
}

export const AvailabilityBadge: React.FC<BadgeProps> = ({ availability, compact, className }) => {
  const tooltip = useAvailabilityTooltip();
  // Plain online/offline needs no words — the dot already said it.
  if (!availability || availability.source === 'session') return null;
  const title = tooltip(availability);
  return (
    <span
      data-testid="availability-badge"
      title={title}
      className={cn('inline-flex min-w-0 items-center gap-1 text-xs text-muted-foreground', className)}
    >
      {availability.emoji && <span aria-hidden>{availability.emoji}</span>}
      {!compact && <span className="truncate">{availability.label}</span>}
    </span>
  );
};
